/**
 * 活動交付區塊（Group / Square 共用）
 * 以 Drawer 取代收合：卡片僅顯示標題與開啟 Drawer 觸發鈕；分享、進行中/已結束、詳情與操作皆在 Drawer 內
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import useSWR from 'swr';
import toast from 'react-hot-toast';
import {
  getActivities,
  getActivityResult,
  getActivityStatistics,
  removeActivity,
  settleActivity,
  type Activity,
} from '@/api/liteActivitiesClient';
import { swrListConfig } from '@/lib/swr-config';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from '@/components/ui/drawer';
import { CalendarCheckIcon } from '@/components/CalendarCheckIcon';
import { ChevronRight, Share2, UsersRound } from 'lucide-react';
import { getLiteJwtToken, setLiteJwtToken } from '../api/liteAuthToken';
import { useLiff } from '../contexts/LiffContext';
import { buildLiteJoinUrl } from '../utils/lite-join-url';

/** 與 tool 羽球編隊共用的 localStorage key，用於帶入參加人員 */
const BADMINTON_IMPORT_STORAGE_KEY = 'badminton-import-participants';

function getJwtToken(): string | null {
  if (typeof window === 'undefined') return null;

  const params = new URLSearchParams(window.location.search);
  const fromUrl = params.get('jwt_token') || params.get('token');
  if (fromUrl) {
    setLiteJwtToken(fromUrl);
    return fromUrl;
  }

  return getLiteJwtToken();
}

function formatTaipeiDateTime(iso: string | null): string {
  if (!iso) return '未設定';
  return new Date(iso).toLocaleString('zh-TW', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTaipeiRange(
  startIso: string | null,
  endIso: string | null
): string {
  if (!startIso && !endIso) return '未設定日期時間';
  return `${formatTaipeiDateTime(startIso)} ~ ${formatTaipeiDateTime(endIso)}`;
}

/** 工具站羽球編隊頁網址（可設 VITE_TOOL_APP_ORIGIN，否則用目前 origin） */
function getBadmintonToolUrl(): string {
  const base =
    import.meta.env.VITE_TOOL_APP_ORIGIN?.trim() ||
    (typeof window !== 'undefined' ? window.location.origin : '');
  return `${base.replace(/\/$/, '')}/tools/badminton-team`;
}

/** 將名單以 base64url 編碼（跨域時透過 URL 傳給工具站，避免 localStorage 不同源無法共用） */
function encodeImportPayload(payload: { name: string }[]): string {
  const json = JSON.stringify(payload);
  const base64 = btoa(unescape(encodeURIComponent(json)));
  return base64.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}

function ImportToBadmintonButton({
  activityId,
  mode,
  squareSuffix,
  onClose,
}: {
  activityId: string;
  mode: 'group' | 'square';
  squareSuffix?: string;
  onClose: () => void;
}) {
  const [loading, setLoading] = useState(false);
  const handleImport = useCallback(async () => {
    const token = getJwtToken();
    if (!token) {
      toast.error('請先登入');
      return;
    }
    setLoading(true);
    try {
      const result = await getActivityResult(activityId, token, {
        ...(mode === 'square' && squareSuffix ? { squareSuffix } : {}),
      });
      const names = (result.participants ?? []).map(
        p => p.userName?.trim() || p.userId || '未知'
      );
      const payload = names.map(name => ({ name }));
      const baseUrl = getBadmintonToolUrl();
      const toolOrigin =
        typeof window !== 'undefined'
          ? new URL(baseUrl, window.location.origin).origin
          : '';
      const isCrossOrigin =
        typeof window !== 'undefined' && window.location.origin !== toolOrigin;
      const url = isCrossOrigin
        ? `${baseUrl}?import=${encodeImportPayload(payload)}`
        : baseUrl;
      if (typeof window !== 'undefined' && !isCrossOrigin) {
        window.localStorage.setItem(
          BADMINTON_IMPORT_STORAGE_KEY,
          JSON.stringify(payload)
        );
      }
      window.open(url, '_blank', 'noopener,noreferrer');
      onClose();
    } catch {
      toast.error('取得參加人員失敗，請稍後再試');
    } finally {
      setLoading(false);
    }
  }, [activityId, mode, squareSuffix, onClose]);

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleImport}
      disabled={loading}
      className="shrink-0"
    >
      <UsersRound className="w-4 h-4 mr-1" aria-hidden />
      {loading ? '載入中…' : '導入編隊工具'}
    </Button>
  );
}

export interface ActivityDeliveryBlockProps {
  /** 'group' 使用 groupId 拉活動；'square' 使用 squareSuffix */
  mode: 'group' | 'square';
  groupId?: string | null;
  squareSuffix?: string | null;
  /** 點擊「建立新活動」 */
  onCreateActivity: () => void;
  /** 點擊「查看參與情況」 */
  onNavigateToDeliver: (activityId: string) => void;
  /** 點擊「進行交付」 */
  onNavigateToResult: (activityId: string) => void;
  /** 選填：Group / Square 皆可傳，活動名稱旁顯示分享按鈕；回傳該活動的報名頁 URL */
  getActivityJoinUrl?: (activityId: string) => string;
}

export function ActivityDeliveryBlock({
  mode,
  groupId,
  squareSuffix,
  onCreateActivity,
  onNavigateToDeliver,
  onNavigateToResult,
  getActivityJoinUrl,
}: ActivityDeliveryBlockProps) {
  const { liff } = useLiff();
  const swrKey =
    mode === 'group' && groupId
      ? ['lite-activities', groupId]
      : mode === 'square' && squareSuffix
        ? ['lite-activities-square', squareSuffix]
        : null;

  const fetcher = useCallback(async (): Promise<Activity[]> => {
    const token = getJwtToken();
    if (!token) throw new Error('請先登入');
    const result = await getActivities(token, {
      page: 1,
      limit: 50,
      ...(mode === 'square' && squareSuffix ? { squareSuffix } : {}),
    });
    return result.activities ?? [];
  }, [mode, squareSuffix]);

  const { data: activities, mutate: mutateActivities } = useSWR<Activity[]>(
    swrKey,
    fetcher,
    { ...swrListConfig, revalidateOnFocus: false }
  );

  const activeActivities = useMemo(
    () => (activities ?? []).filter(a => a.status === 'active'),
    [activities]
  );

  /** 活動交付只顯示已發佈(active)與已結束(inactive) */
  const deliverActivities = useMemo(() => {
    const list = (activities ?? []).filter(
      a => a.status === 'active' || a.status === 'inactive'
    );
    return list.sort((a, b) => {
      if (a.status !== b.status) return a.status === 'active' ? -1 : 1;
      return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
    });
  }, [activities]);

  const [statistics, setStatistics] = useState<
    Record<
      string,
      {
        delivered: number;
        undelivered: number;
        cancelled: number;
        total: number;
        allHaveStatus: boolean;
      }
    >
  >({});
  const [productTotals, setProductTotals] = useState<
    Record<string, Record<string, number>>
  >({});
  const [settlingActivityId, setSettlingActivityId] = useState<string | null>(
    null
  );
  const [removingActivityId, setRemovingActivityId] = useState<string | null>(
    null
  );
  const [confirmAction, setConfirmAction] = useState<{
    type: 'settle' | 'remove';
    activityId: string;
  } | null>(null);
  /** 目前開啟的活動詳情 Drawer（取代收合） */
  const [drawerActivityId, setDrawerActivityId] = useState<string | null>(null);

  const squareContext = useMemo(
    () => (mode === 'square' && squareSuffix ? { squareSuffix } : undefined),
    [mode, squareSuffix]
  );

  const activeActivityIdsKey = useMemo(
    () => activeActivities.map(a => a.id).join('|'),
    [activeActivities]
  );

  useEffect(() => {
    if (deliverActivities.length === 0) {
      setStatistics({});
      return;
    }
    const loadStatistics = async () => {
      const token = getJwtToken();
      if (!token) return;
      const targetActivities = deliverActivities;
      const entries = await Promise.all(
        targetActivities.map(async activity => {
          try {
            const stats = await getActivityStatistics(
              activity.id,
              token,
              squareContext
            );
            return [activity.id, stats] as const;
          } catch {
            return null;
          }
        })
      );
      const statsMap: Record<
        string,
        {
          delivered: number;
          undelivered: number;
          cancelled: number;
          total: number;
          allHaveStatus: boolean;
        }
      > = {};
      entries.forEach(entry => {
        if (!entry) return;
        const [id, stats] = entry;
        statsMap[id] = stats;
      });
      setStatistics(statsMap);
    };
    loadStatistics();
  }, [deliverActivities, activeActivityIdsKey, squareContext]);

  useEffect(() => {
    if (activeActivities.length === 0) return;
    const loadProductTotals = async () => {
      const token = getJwtToken();
      if (!token) return;
      const entries = await Promise.all(
        activeActivities.map(async activity => {
          try {
            const result = await getActivityResult(
              activity.id,
              token,
              squareContext
            );
            const totals: Record<string, number> = {};
            Object.entries(result.productSummary ?? {}).forEach(
              ([productKey, summary]) => {
                totals[productKey] = summary.totalQuantity ?? 0;
              }
            );
            return [activity.id, totals] as const;
          } catch {
            return null;
          }
        })
      );
      setProductTotals(prev => {
        const next = { ...prev };
        entries.forEach(entry => {
          if (!entry) return;
          const [activityId, totals] = entry;
          next[activityId] = totals;
        });
        return next;
      });
    };
    loadProductTotals();
  }, [activeActivityIdsKey, activeActivities, squareContext]);

  const handleSettleActivity = async (activityId: string) => {
    try {
      setSettlingActivityId(activityId);
      const token = getJwtToken();
      if (!token) {
        toast.error('請先登入');
        return;
      }
      await settleActivity(activityId, token, squareContext);
      toast.success('活動已結束', { duration: 2000 });
      await mutateActivities();
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : '結束活動失敗，請稍後再試';
      toast.error(msg);
    } finally {
      setSettlingActivityId(null);
    }
  };

  const handleRemoveActivity = async (activityId: string) => {
    try {
      setRemovingActivityId(activityId);
      const token = getJwtToken();
      if (!token) {
        toast.error('請先登入');
        return;
      }
      mutateActivities(
        (current: Activity[] | undefined) =>
          (current ?? []).filter(a => a.id !== activityId),
        { revalidate: false }
      );
      await removeActivity(activityId, token, squareContext);
      toast.success('活動已移除', { duration: 2000 });
    } catch (error: unknown) {
      const msg =
        error instanceof Error ? error.message : '移除活動失敗，請稍後再試';
      toast.error(msg);
      await mutateActivities();
    } finally {
      setRemovingActivityId(null);
    }
  };

  const confirmTitle =
    confirmAction?.type === 'settle'
      ? '確認結束活動'
      : confirmAction?.type === 'remove'
        ? '確認移除活動'
        : '';
  const confirmDescription =
    confirmAction?.type === 'settle'
      ? '結束後將無法再修改此活動。'
      : confirmAction?.type === 'remove'
        ? '移除後將無法恢復。'
        : '';

  if (deliverActivities.length === 0) {
    return (
      <div className="text-center py-6">
        <div className="mb-4 flex justify-center">
          <CalendarCheckIcon className="w-14 h-14 text-gray-700" />
        </div>
        <div className="text-xl font-semibold text-gray-900 mb-2">
          目前沒有活動
        </div>
        <div className="text-sm text-muted-foreground mb-6">
          目前沒有已啟動的活動需要交付
        </div>
        <Button onClick={onCreateActivity}>建立新活動</Button>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-4">
        {deliverActivities.map(activity => {
          const stats = statistics[activity.id];
          const isActive = activity.status === 'active';
          const isEnded = !isActive;
          const endDate = activity.endDate ? new Date(activity.endDate) : null;
          const shouldShowEarlyEnd =
            isActive && (!endDate || new Date() < endDate);
          const endActionLabel = shouldShowEarlyEnd ? '提早結束' : '結束';
          const canRemoveAfterEnded =
            isEnded && !!stats && stats.undelivered === 0;

          const totalsForActivity = productTotals[activity.id] ?? {};
          const getCurrentQuantity = (productId: string): number => {
            const direct = totalsForActivity[productId] ?? 0;
            const fromSpecs = Object.entries(totalsForActivity).reduce(
              (sum, [key, qty]) =>
                key.startsWith(productId + ':') ? sum + qty : sum,
              0
            );
            return direct + fromSpecs;
          };
          const hasAnyConfigured = (activity.productList ?? []).some(p => {
            if ('maxTotalQuantity' in p && p.specs?.length)
              return (p.maxTotalQuantity ?? 0) > 0;
            return 'quantity' in p && (p.quantity ?? 0) > 0;
          });
          const productRows =
            isActive && activity.productList
              ? activity.productList
                  .map(p => {
                    const configured =
                      'maxTotalQuantity' in p && p.specs?.length
                        ? (p.maxTotalQuantity ?? 0)
                        : 'quantity' in p
                          ? (p.quantity ?? 0)
                          : 0;
                    return {
                      id: p.id,
                      name: p.name,
                      current: getCurrentQuantity(p.id),
                      configured,
                    };
                  })
                  .filter(p => (hasAnyConfigured ? p.configured > 0 : true))
              : [];

          return (
            <Card key={activity.id} className="shadow-none border">
              <CardHeader className="py-3">
                <button
                  type="button"
                  onClick={() => setDrawerActivityId(activity.id)}
                  className="flex items-center justify-between gap-2 w-full text-left rounded-md hover:bg-muted/50 -m-2 p-2 transition-colors"
                >
                  <div className="min-w-0 flex-1">
                    <CardTitle className="text-xl truncate">
                      {activity.title}
                    </CardTitle>
                    {activity.description && (
                      <CardDescription className="mt-1 line-clamp-2">
                        {activity.description}
                      </CardDescription>
                    )}
                    <CardDescription className="mt-1 text-sm text-muted-foreground">
                      {formatTaipeiRange(activity.startDate, activity.endDate)}
                    </CardDescription>
                  </div>
                  <ChevronRight
                    className="h-5 w-5 shrink-0 text-muted-foreground"
                    aria-hidden
                  />
                </button>
              </CardHeader>
              <CardFooter className="flex flex-wrap gap-2">
                {isActive ? (
                  <>
                    <Button
                      onClick={() => onNavigateToDeliver(activity.id)}
                      className="flex-1"
                    >
                      查看參與情況
                    </Button>
                    <Button
                      variant="destructive"
                      onClick={() =>
                        setConfirmAction({
                          type: 'settle',
                          activityId: activity.id,
                        })
                      }
                      disabled={settlingActivityId === activity.id}
                      className="flex-1"
                    >
                      {settlingActivityId === activity.id
                        ? '處理中...'
                        : endActionLabel}
                    </Button>
                  </>
                ) : (
                  <>
                    {canRemoveAfterEnded ? (
                      <Button
                        variant="destructive"
                        onClick={() =>
                          setConfirmAction({
                            type: 'remove',
                            activityId: activity.id,
                          })
                        }
                        disabled={removingActivityId === activity.id}
                        className="w-full"
                      >
                        {removingActivityId === activity.id
                          ? '移除中...'
                          : '移除活動'}
                      </Button>
                    ) : (
                      <Button
                        className="w-full"
                        onClick={() => onNavigateToResult(activity.id)}
                      >
                        進行交付
                      </Button>
                    )}
                  </>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>

      {/* 活動詳情 Drawer（取代收合）：分享、進行中/已結束、詳情與操作 */}
      <Drawer
        open={!!drawerActivityId}
        onOpenChange={open => !open && setDrawerActivityId(null)}
      >
        <DrawerContent>
          {(() => {
            const activity = deliverActivities.find(
              a => a.id === drawerActivityId
            );
            if (!activity) return null;
            const stats = statistics[activity.id];
            const isActive = activity.status === 'active';
            const isEnded = !isActive;
            const endDate = activity.endDate
              ? new Date(activity.endDate)
              : null;
            const shouldShowEarlyEnd =
              isActive && (!endDate || new Date() < endDate);
            const endActionLabel = shouldShowEarlyEnd ? '提早結束' : '結束';
            const canRemoveAfterEnded =
              isEnded && !!stats && stats.undelivered === 0;
            const totalsForActivity = productTotals[activity.id] ?? {};
            const getCurrentQuantity = (productId: string): number => {
              const direct = totalsForActivity[productId] ?? 0;
              const fromSpecs = Object.entries(totalsForActivity).reduce(
                (sum, [key, qty]) =>
                  key.startsWith(productId + ':') ? sum + qty : sum,
                0
              );
              return direct + fromSpecs;
            };
            const hasAnyConfigured = (activity.productList ?? []).some(p => {
              if ('maxTotalQuantity' in p && p.specs?.length)
                return (p.maxTotalQuantity ?? 0) > 0;
              return 'quantity' in p && (p.quantity ?? 0) > 0;
            });
            const productRows =
              isActive && activity.productList
                ? activity.productList
                    .map(p => {
                      const configured =
                        'maxTotalQuantity' in p && p.specs?.length
                          ? (p.maxTotalQuantity ?? 0)
                          : 'quantity' in p
                            ? (p.quantity ?? 0)
                            : 0;
                      return {
                        id: p.id,
                        name: p.name,
                        current: getCurrentQuantity(p.id),
                        configured,
                      };
                    })
                    .filter(p => (hasAnyConfigured ? p.configured > 0 : true))
                : [];
            const joinUrl = activity.shortUrl
              ? buildLiteJoinUrl({
                  shortUrl: activity.shortUrl,
                  origin:
                    typeof window !== 'undefined' ? window.location.origin : '',
                  activityId: activity.id,
                })
              : getActivityJoinUrl?.(activity.id);
            const handleShare = () => {
              if (!joinUrl) return;
              const shareText = `邀請你一起參與：${activity.title}\n${joinUrl}`;
              const shareTargetPicker =
                liff &&
                'shareTargetPicker' in liff &&
                typeof (
                  liff as unknown as {
                    shareTargetPicker?: (m: unknown[]) => Promise<unknown>;
                  }
                ).shareTargetPicker === 'function'
                  ? (
                      liff as unknown as {
                        shareTargetPicker: (m: unknown[]) => Promise<unknown>;
                      }
                    ).shareTargetPicker
                  : null;
              if (shareTargetPicker) {
                shareTargetPicker([{ type: 'text', text: shareText }])
                  .then(result => {
                    if (result !== undefined && result !== null) {
                      toast.success('已分享至 LINE', { duration: 2000 });
                    }
                    setDrawerActivityId(null);
                  })
                  .catch(() => {
                    const lineShareUrl = `https://line.me/R/share?text=${encodeURIComponent(shareText)}`;
                    window.open(lineShareUrl, '_blank', 'noopener,noreferrer');
                    toast.success('已開啟 LINE 分享', { duration: 2000 });
                  });
              } else {
                const lineShareUrl = `https://line.me/R/share?text=${encodeURIComponent(shareText)}`;
                window.open(lineShareUrl, '_blank', 'noopener,noreferrer');
                toast.success('已開啟 LINE 分享', { duration: 2000 });
              }
            };
            return (
              <>
                <DrawerHeader>
                  <DrawerTitle className="pr-8">{activity.title}</DrawerTitle>
                  <div className="flex flex-wrap items-center justify-center gap-2 pt-2">
                    <span
                      className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        isActive
                          ? 'bg-green-100 text-green-800'
                          : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {isActive ? '進行中' : '已結束'}
                    </span>
                    {joinUrl && isActive && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={handleShare}
                        className="shrink-0"
                      >
                        <Share2 className="w-4 h-4 mr-1" aria-hidden />
                        分享
                      </Button>
                    )}
                    {isEnded && (
                      <ImportToBadmintonButton
                        activityId={activity.id}
                        mode={mode}
                        squareSuffix={squareSuffix ?? undefined}
                        onClose={() => setDrawerActivityId(null)}
                      />
                    )}
                  </div>
                  <div
                    className="w-[80%] mx-auto border-b border-gray-200 pt-3"
                    aria-hidden
                  />
                </DrawerHeader>
                <div className="px-4 space-y-3 min-h-[2rem] pb-[max(1.5rem,env(safe-area-inset-bottom))]">
                  {isActive ? (
                    <div className="space-y-1 text-sm">
                      {productRows.length === 0 ? (
                        <div className="text-muted-foreground">
                          尚無商品訂閱
                        </div>
                      ) : (
                        productRows.map(p => (
                          <div
                            key={p.id}
                            className="flex items-center gap-3 w-full"
                          >
                            <span className="min-w-0 flex-1 truncate text-gray-700">
                              {p.name}
                            </span>
                            <span className="ml-auto shrink-0 text-gray-900 font-medium tabular-nums">
                              {p.current}
                              {p.configured > 0 ? `/${p.configured}` : ''}
                            </span>
                          </div>
                        ))
                      )}
                    </div>
                  ) : stats ? (
                    <div className="space-y-1 text-sm">
                      <div className="text-green-600 font-medium">
                        總人數：{stats.total}人
                      </div>
                      <div className="text-yellow-600 font-medium">
                        未交付：{stats.undelivered}人
                      </div>
                      <div className="text-red-600 font-medium">
                        棄單：{stats.cancelled}人
                      </div>
                    </div>
                  ) : (
                    <div className="text-sm text-muted-foreground">
                      載入統計中...
                    </div>
                  )}
                </div>
              </>
            );
          })()}
        </DrawerContent>
      </Drawer>

      <Drawer
        open={!!confirmAction}
        onOpenChange={open => !open && setConfirmAction(null)}
      >
        <DrawerContent>
          <DrawerHeader>
            <DrawerTitle>{confirmTitle}</DrawerTitle>
            <DrawerDescription>{confirmDescription}</DrawerDescription>
          </DrawerHeader>
          <DrawerFooter>
            <Button
              variant="outline"
              onClick={() => setConfirmAction(null)}
              className="w-full"
            >
              取消
            </Button>
            <Button
              variant="destructive"
              className="w-full"
              onClick={async () => {
                const action = confirmAction;
                if (!action) return;
                setConfirmAction(null);
                if (action.type === 'settle') {
                  await handleSettleActivity(action.activityId);
                } else {
                  await handleRemoveActivity(action.activityId);
                }
              }}
            >
              確認
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
    </>
  );
}
